import { useState, useEffect } from "react"
import { collection, query, where, orderBy, onSnapshot, doc, getDoc} from "firebase/firestore"
import { chatCollectionModel } from "../firebase/firebase-config"
import type { ChatMessage, ChatHistory, UserData } from "../types/adminmessages"
import { db } from "../firebase/firebase-service";

export function useChatMessages(chatId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!chatId) {
      setMessages([])
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      const messagesRef = collection(db, chatCollectionModel.adminChats!, chatId, "messages")
      const q = query(messagesRef, orderBy("sortingTimeStamp", "asc"))
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const newMessages: ChatMessage[] = []

          snapshot.forEach((doc) => {
            const data = doc.data()
            newMessages.push({
              id: doc.id,
              text: data.text,
              senderId: data.senderId,
              timestamp: data.timestamp,
              sortingTimeStamp: data.sortingTimeStamp,
              read: data.read,
              imageURL: data.imageURL
            })
          })
          setMessages(newMessages)
          setLoading(false)
        },
        (err) => {
          setError(err)
          setLoading(false)
        },
      )

      return () => unsubscribe()
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      setLoading(false)
    }
  }, [chatId])

  return { messages, loading, error }
}

export function useChatList(userId: string) {
  const [chats, setChats] = useState<ChatHistory[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!userId) {
      setChats([])
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      const chatsRef = collection(db, chatCollectionModel.adminChats!)
      const q = query(chatsRef, where("participants", "array-contains", userId))

      const unsubscribe = onSnapshot(
        q,
        async (snapshot) => {
          const chatList: ChatHistory[] = []

          for (const docSnapshot of snapshot.docs) {
            const chatData = docSnapshot.data()
            const participants = chatData.participants as string[]

            // Get the other participant (not the current user)
            const otherParticipantId = participants.find((id) => id !== userId)

            if (otherParticipantId) {
              try {
                // Get user details for the other participant
                const userRef = doc(db, chatCollectionModel.adminChats!, otherParticipantId)
                const userSnap = await getDoc(userRef)

                if (userSnap.exists()) {
                  const userData = userSnap.data() as UserData

                  chatList.push({
                    chatId: docSnapshot.id,
                    participants: chatData.participants,
                    agentId: chatData.agentId || (userId.includes("agent") ? userId : otherParticipantId),
                    agentName: userId.includes("agent") ? "Support" : userData.fullName,
                    agentImageUrl: userData.imageUrl,
                    travellerId: chatData.travellerId || (userId.includes("agent") ? otherParticipantId : userId),
                    travellerName: userId.includes("agent") ? userData.fullName : "You",
                    travellerImageUrl: userData.imageUrl,
                    otherParticipantId: otherParticipantId,
                    otherParticipantName: userData.fullName,
                    otherParticipantImageUrl: userData.imageUrl,
                    cityName: chatData.cityName || "Unknown",
                    lastMessagedBy: chatData.lastMessagedBy,
                    lastUpdated: chatData.lastUpdated,
                    unreadCount: chatData.unreadCount,
                    tripStartDate: chatData.tripStartDate,
                    tripEndDate: chatData.tripEndDate,
                  })
                }
              } catch (err) {
                console.error("Error fetching user data:", err)
              }
            }
          }

          // Sort by lastUpdated
          chatList.sort((a, b) => (b.lastUpdated || 0) - (a.lastUpdated || 0))

          setChats(chatList)
          setLoading(false)
        },
        (err) => {
          setError(err)
          setLoading(false)
        },
      )

      return () => unsubscribe()
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      setLoading(false)
    }
  }, [userId])

  return { chats, loading, error }
}